import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { PreparedFileUpload } from 'lib/utils/dataUrl';

export enum ListItemStatus {
  Pending = 'pending',
  Active = 'active',
  Archived = 'archived',
}

export enum EmailAddressMatchType {
  Email = 'email_address',
  Domain = 'domain',
}

interface ListItemCoreAttributes {
  status?: ListItemStatus;
  'environment-id'?: string;
  'list-id'?: string;
  'match-count'?: number;
  'archived-at'?: string;
  'updated-at'?: string;
  'created-at'?: string;
  'source-id'?: string;
  'redacted-at'?: string;
  'last-matched-at'?: string;
}

export interface ListItemGovernmentIdNumberAttributes extends ListItemCoreAttributes {
  'id-class': GovernmentIdClass | null;
  'id-number': string | null;
}

export interface ListItemIpAddressAttributes extends ListItemCoreAttributes {
  value: string | null;
}

export interface ListItemNameAttributes extends ListItemCoreAttributes {
  'name-first': string | null;
  'name-last': string | null;
}

export interface ListItemPhoneNumberAttributes extends ListItemCoreAttributes {
  value: string | null;
}

export interface ListItemEmailAddressAttributes extends ListItemCoreAttributes {
  value: string | null;
  'match-type': EmailAddressMatchType;
}

export interface ListItemGeolocationAttributes extends ListItemCoreAttributes {
  latitude: number | null;
  longitude: number | null;
  'radius-meters': number | null;
}

export interface ListItemBrowserFingerprintAttributes extends ListItemCoreAttributes {
  value: string | null;
}

export interface ListItemDeviceFingerprintAttributes extends ListItemCoreAttributes {
  value: string | null;
}

export interface ListItemStringAttributes extends ListItemCoreAttributes {
  value: string | null;
}

interface FacePhoto {
  url: string;
}

interface SelfiePhoto {
  url: string;
}

export interface ListItemFaceRequestAttributes extends ListItemCoreAttributes {
  'face-photo-upload': PreparedFileUpload;
}

export interface ListItemFaceResponseAttributes extends ListItemCoreAttributes {
  'face-photo': FacePhoto | null;
}

export interface ListItemSelfieBackgroundRequestAttributes extends ListItemCoreAttributes {
  'selfie-photo-upload': PreparedFileUpload;
}
export interface ListItemSelfieBackgroundResponseAttributes extends ListItemCoreAttributes {
  'selfie-photo': SelfiePhoto | null;
}

export interface ListItemCountryAttributes extends ListItemCoreAttributes {
  'country-code': CountryCode | null;
}

export interface ListItemFieldAttributes extends ListItemCoreAttributes {
  'field-value': string | null;
}

export interface ListItemRelationships {
  // should be only ApiModel.User but our "User" model says ApiModel.CurrentUser is possible
  creator: ApiRelationship<ApiModel.User | ApiModel.CurrentUser>;
  source?: ApiRelationship<
    ApiModel.Account | ApiModel.Inquiry | ApiModel.Transaction | ApiModel.VerificationSelfie
  >;
}

export type ListItemGovernmentIdNumber = ApiResource<
  ListItemGovernmentIdNumberAttributes,
  ListItemRelationships,
  ApiModel.ListItemGovernmentIdNumber
>;

export type ListItemIpAddress = ApiResource<
  ListItemIpAddressAttributes,
  ListItemRelationships,
  ApiModel.ListItemIpAddress
>;

export type ListItemName = ApiResource<
  ListItemNameAttributes,
  ListItemRelationships,
  ApiModel.ListItemName
>;

export type ListItemPhoneNumber = ApiResource<
  ListItemPhoneNumberAttributes,
  ListItemRelationships,
  ApiModel.ListItemPhoneNumber
>;

export type ListItemEmailAddress = ApiResource<
  ListItemEmailAddressAttributes,
  ListItemRelationships,
  ApiModel.ListItemEmailAddress
>;

export type ListItemGeolocation = ApiResource<
  ListItemGeolocationAttributes,
  ListItemRelationships,
  ApiModel.ListItemGeolocation
>;

export type ListItemBrowserFingerprint = ApiResource<
  ListItemBrowserFingerprintAttributes,
  ListItemRelationships,
  ApiModel.ListItemBrowserFingerprint
>;

export type ListItemDeviceFingerprint = ApiResource<
  ListItemDeviceFingerprintAttributes,
  ListItemRelationships,
  ApiModel.ListItemDeviceFingerprint
>;

export type ListItemString = ApiResource<
  ListItemStringAttributes,
  ListItemRelationships,
  ApiModel.ListItemString
>;

export type ListItemSelfieBackground = ApiResource<
  ListItemSelfieBackgroundRequestAttributes | ListItemSelfieBackgroundResponseAttributes,
  ListItemRelationships,
  ApiModel.ListItemSelfieBackground
>;

export type ListItemFace = ApiResource<
  ListItemFaceRequestAttributes | ListItemFaceResponseAttributes,
  ListItemRelationships,
  ApiModel.ListItemFace
>;

export type ListItemCountry = ApiResource<
  ListItemCountryAttributes,
  ListItemRelationships,
  ApiModel.ListItemCountry
>;

export type ListItemField = ApiResource<
  ListItemFieldAttributes,
  ListItemRelationships,
  ApiModel.ListItemField
>;

export type ListItem =
  | ListItemGovernmentIdNumber
  | ListItemIpAddress
  | ListItemName
  | ListItemPhoneNumber
  | ListItemEmailAddress
  | ListItemGeolocation
  | ListItemBrowserFingerprint
  | ListItemDeviceFingerprint
  | ListItemString
  | ListItemFace
  | ListItemCountry
  | ListItemField
  | ListItemSelfieBackground;
